import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';

interface TocEntry {
  id: string;
  title: string;
}

interface TocContextValue {
  entries: TocEntry[];
  activeId: string | null;
  scrollRef: RefObject<HTMLDivElement | null>;
  register: (entry: TocEntry) => void;
  unregister: (id: string) => void;
  setActive: (id: string) => void;
}

const TocContext = createContext<TocContextValue | null>(null);

export function TocProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const register = useCallback((entry: TocEntry) => {
    setEntries((prev) => {
      if (prev.some((e) => e.id === entry.id)) return prev;
      return [...prev, entry];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const setActive = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  const value = useMemo(
    () => ({ entries, activeId, scrollRef, register, unregister, setActive }),
    [entries, activeId, register, unregister, setActive],
  );

  return <TocContext.Provider value={value}>{children}</TocContext.Provider>;
}

export function useToc(): TocContextValue {
  const ctx = useContext(TocContext);
  if (!ctx) throw new Error('useToc must be used inside a TocProvider');
  return ctx;
}
